
import React, { useState } from 'react';
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { X, ExternalLink, AlertCircle, Image, Loader2, Link, RefreshCw } from 'lucide-react';

interface WebsitePreviewFieldProps {
  websitePreviewUrl: string;
  isUploading: boolean;
  uploadError?: string;
  onFileSelect?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onRemove: () => void;
  onExpand: () => void;
  onExternalUrl?: (url: string) => void;
  disableUpload?: boolean;
}

const WebsitePreviewField: React.FC<WebsitePreviewFieldProps> = ({
  websitePreviewUrl,
  isUploading,
  uploadError,
  onFileSelect,
  onRemove,
  onExpand,
  onExternalUrl,
  disableUpload = false
}) => {
  const [previewError, setPreviewError] = useState(false);
  const [externalUrlMode, setExternalUrlMode] = useState(false);
  const [externalUrl, setExternalUrl] = useState('');
  
  const handleUseExternalUrl = () => {
    if (onExternalUrl && externalUrl) {
      onExternalUrl(externalUrl);
      setExternalUrlMode(false);
    }
  };
  
  return (
    <div>
      <label htmlFor="website-preview-upload" className="block mb-2">
        Website Preview Image
        {disableUpload && <span className="text-xs text-orange-500 ml-1">(External URLs only - upload requires admin rights)</span>}
      </label>
      <div className="flex flex-col gap-3">
        <div className="flex items-center gap-3">
          <div className="flex-1">
            {!websitePreviewUrl && !externalUrlMode ? (
              <div className="relative">
                {!disableUpload && onFileSelect && (
                  <Input
                    id="website-preview-upload"
                    type="file"
                    onChange={onFileSelect}
                    accept="image/*"
                    disabled={isUploading || disableUpload}
                  />
                )}
                <div className="flex mt-2 gap-2">
                  <p className="text-sm text-gray-500">
                    {disableUpload ? 
                      "Direct uploads are restricted to administrators. Please use an external URL for your website preview image." :
                      "Upload a screenshot of your website to display on your company profile. The image can be viewed in full size when clicked."
                    }
                  </p>
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={() => setExternalUrlMode(true)}
                    className="whitespace-nowrap"
                  >
                    <Link className="h-4 w-4 mr-1" />
                    Use External URL
                  </Button>
                </div>
                {uploadError && (
                  <div className="flex items-center gap-2 mt-2 text-sm text-red-500">
                    <AlertCircle className="h-4 w-4" />
                    <span>{uploadError}</span>
                  </div>
                )}
              </div>
            ) : externalUrlMode ? (
              <div className="space-y-2">
                <div className="flex gap-2">
                  <Input
                    type="url"
                    placeholder="Enter image URL (https://...)"
                    value={externalUrl}
                    onChange={(e) => setExternalUrl(e.target.value)}
                    className="flex-1"
                  />
                  <Button onClick={handleUseExternalUrl} disabled={!externalUrl}>
                    <ExternalLink className="h-4 w-4 mr-2" />
                    Use URL
                  </Button>
                  <Button variant="outline" onClick={() => setExternalUrlMode(false)}>
                    <X className="h-4 w-4" />
                  </Button>
                </div>
                <p className="text-xs text-gray-500">
                  Make sure the image URL is publicly accessible and from a reliable source.
                  For best results, use URLs that end with .jpg, .png, or .webp
                </p>
              </div>
            ) : (
              <div className="flex items-center justify-between p-2 border rounded-md">
                <div className="flex items-center gap-2">
                  <div 
                    className="h-12 w-16 rounded overflow-hidden bg-gray-100 flex-shrink-0 cursor-pointer relative"
                    onClick={onExpand}
                  >
                    {previewError ? (
                      <div className="absolute inset-0 flex items-center justify-center bg-gray-200">
                        <Image className="h-5 w-5 text-gray-400" />
                      </div>
                    ) : (
                      <img 
                        src={`${websitePreviewUrl}?t=${Date.now()}`} // Add cache-busting
                        alt="Website preview" 
                        className="h-full w-full object-cover" 
                        onError={() => setPreviewError(true)}
                      />
                    )}
                  </div>
                  <span className="text-sm text-gray-700">Website preview {previewError ? 'unavailable' : 'uploaded'} (click to expand)</span>
                </div>
                <div className="flex gap-1">
                  {previewError ? (
                    <Button variant="outline" size="icon" onClick={() => setExternalUrlMode(true)}>
                      <Link className="h-4 w-4" />
                    </Button>
                  ) : (
                    <>
                      <Button variant="outline" size="icon" onClick={() => {
                        // Force image reload by triggering state update
                        setPreviewError(false);
                      }}>
                        <RefreshCw className="h-4 w-4" />
                      </Button>
                      <a 
                        href={websitePreviewUrl} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        onClick={(e) => {
                          if (previewError) {
                            e.preventDefault();
                            alert('Preview image cannot be displayed. It may not be accessible.');
                          }
                        }}
                      >
                        <Button variant="outline" size="icon">
                          <ExternalLink className="h-4 w-4" />
                        </Button>
                      </a>
                    </>
                  )}
                  <Button variant="outline" size="icon" onClick={onRemove}>
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            )}
          </div>
          {isUploading && (
            <div className="text-sm text-blue-600 flex items-center gap-1">
              <Loader2 className="h-4 w-4 animate-spin" />
              <span>Uploading...</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default WebsitePreviewField;
